import { useEffect, useRef, useState } from 'react'

import classNames from 'classnames'
import { createPortal } from 'react-dom'

import { useOnEscPress } from '@/hooks/useOnEscPress'

import './Drawer.scss'

interface DrawerProps {
  isOpen: boolean
  close: () => void
  direction?: 'right' | 'left' | 'top' | 'bottom'
  closeOnOutsideClick?: boolean
  children?: React.ReactNode
}

export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  close,
  direction = 'left',
  closeOnOutsideClick = true,
  children,
}) => {
  const ref = useRef(null)

  const [show, setShow] = useState(false)
  const [overlay, setOverlay] = useState(false)

  useOnEscPress({ onEscPress: close })

  useEffect(() => {
    const timeouts: NodeJS.Timeout[] = []
    let animationFrame: number | undefined

    if (isOpen) {
      animationFrame = window.requestAnimationFrame(() => {
        setShow(true)
        setOverlay(true)
      })
    } else {
      timeouts.push(setTimeout(() => setShow(false), 300))
      timeouts.push(setTimeout(() => setOverlay(false), 500))
    }

    return () => {
      timeouts.forEach(clearTimeout)
      if (animationFrame) window.cancelAnimationFrame(animationFrame)
    }
  }, [isOpen])

  if (overlay && !show) {
    return createPortal(<div className="overlay is-open"></div>, document.body)
  }

  if (!show) return null

  return createPortal(
    <div
      ref={ref}
      className={classNames('overlay', { ['is-open']: show })}
      onMouseDown={(e) => {
        if (closeOnOutsideClick && e.target === ref.current) {
          close()
        }
      }}
    >
      <div
        className={classNames('drawer panel', 'direction', direction, {
          [`animate-in-${direction}`]: show,
          [`animate-out-${direction}`]: !isOpen,
          ['is-open']: show,
        })}
      >
        {children}
      </div>
    </div>,
    document.body,
  )
}
